import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  HomeIcon,
  UserGroupIcon,
  CalendarIcon,
  ChartBarIcon,
  CogIcon,
  UserIcon,
  TruckIcon,
  ClipboardDocumentListIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: HomeIcon },
  {
    name: 'User Management',
    icon: UserGroupIcon,
    children: [
      { name: 'All Users', href: '/admin/users' },
      { name: 'Pending Drivers', href: '/admin/pending-drivers' },
      { name: 'Driver Profiles', href: '/admin/drivers' },
      { name: 'Rider Profiles', href: '/admin/riders' },
      { name: 'Admin Profiles', href: '/admin/admins' },
    ],
  },
  {
    name: 'Rides Management',
    icon: CalendarIcon,
    children: [
      { name: 'All Rides', href: '/admin/rides' },
      { name: 'Unmatched Requests', href: '/admin/unmatched-requests' },
      { name: 'Manual Matching', href: '/admin/manual-matching' },
    ],
  },
  { name: 'Bookings', href: '/admin/bookings', icon: ClipboardDocumentListIcon },
  { name: 'Analytics & Reports', href: '/admin/analytics', icon: ChartBarIcon },
  { name: 'App Settings', href: '/admin/settings', icon: CogIcon },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const location = useLocation();
  const { currentUser, logout } = useAuth();

  const toggleExpanded = (itemName) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const isActive = (href) => {
    return location.pathname === href;
  };

  const isParentActive = (children) => {
    return children?.some(child => location.pathname === child.href);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile sidebar */}
      <div className={`no-print fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-slate-900">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent 
            navigation={navigation}
            expandedItems={expandedItems}
            toggleExpanded={toggleExpanded}
            isActive={isActive}
            isParentActive={isParentActive}
            currentUser={currentUser}
            logout={logout}
          />
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="no-print hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-slate-900">
          <SidebarContent 
            navigation={navigation}
            expandedItems={expandedItems}
            toggleExpanded={toggleExpanded}
            isActive={isActive}
            isParentActive={isParentActive}
            currentUser={currentUser}
            logout={logout}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="admin-print-content lg:pl-64 flex flex-col flex-1">
        <div className="no-print relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              <div className="w-full flex md:ml-0">
                <div className="relative w-full text-gray-400 focus-within:text-gray-600">
                  <div className="flex items-center h-16">
                    <h1 className="text-xl font-semibold text-gray-900">Church Carpool Admin</h1>
                  </div>
                </div>
              </div>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              <div className="flex items-center">
                <UserCircleIcon className="h-8 w-8 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700">{currentUser?.firstName} {currentUser?.lastName}</span>
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1">
          <div className="admin-print-main py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ navigation, expandedItems, toggleExpanded, isActive, isParentActive, currentUser, logout }) {
  return (
    <>
      <div className="flex items-center h-16 flex-shrink-0 px-4 bg-slate-800">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">CA</span>
            </div>
          </div>
          <div className="ml-3">
            <h1 className="text-white font-semibold text-lg">Church Admin</h1>
            <p className="text-slate-300 text-xs">Management Portal</p>
          </div>
        </div>
      </div>
      
      <div className="mt-5 flex-1 h-0 overflow-y-auto">
        <nav className="px-2 space-y-1">
          {navigation.map((item) => (
            <div key={item.name}>
              {!item.children ? (
                <Link
                  to={item.href}
                  className={classNames(
                    isActive(item.href)
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white',
                    'group flex items-center px-2 py-2 text-sm font-medium rounded-md'
                  )}
                >
                  <item.icon
                    className={classNames(
                      isActive(item.href) ? 'text-white' : 'text-slate-400 group-hover:text-white',
                      'mr-3 flex-shrink-0 h-5 w-5'
                    )}
                  />
                  {item.name}
                </Link>
              ) : (
                <div>
                  <button
                    onClick={() => toggleExpanded(item.name)}
                    className={classNames(
                      isParentActive(item.children)
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white',
                      'group w-full flex items-center pl-2 pr-1 py-2 text-left text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500'
                    )}
                  >
                    <item.icon
                      className={classNames(
                        isParentActive(item.children) ? 'text-white' : 'text-slate-400 group-hover:text-white',
                        'mr-3 flex-shrink-0 h-5 w-5'
                      )}
                    />
                    <span className="flex-1">{item.name}</span>
                    <svg
                      className={classNames(
                        expandedItems[item.name] ? 'text-white rotate-90' : 'text-slate-400',
                        'ml-3 flex-shrink-0 h-5 w-5 transform group-hover:text-white transition-colors ease-in-out duration-150'
                      )}
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path d="M6 6L14 10l-8 4V6z" fill="currentColor" />
                    </svg>
                  </button>
                  {expandedItems[item.name] && (
                    <div className="mt-1 space-y-1">
                      {item.children.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={classNames(
                            isActive(subItem.href)
                              ? 'bg-slate-800 text-white'
                              : 'text-slate-300 hover:bg-slate-700 hover:text-white',
                            'group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md'
                          )}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>
      
      <div className="flex-shrink-0 flex border-t border-slate-700 p-4">
        <div className="flex-shrink-0 group block">
          <div className="flex items-center">
            <div>
              <UserCircleIcon className="inline-block h-9 w-9 text-slate-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-white">
                {currentUser?.firstName} {currentUser?.lastName}
              </p>
              <button
                onClick={logout}
                className="flex items-center text-xs font-medium text-slate-300 hover:text-white"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 